import React from 'react';
import { Flag } from 'lucide-react';
import {
  describeReceivableDueAnchor,
  type PaymentDueFlag,
} from '../../utils/paymentDueFlags';
import { HoverTip } from './SupplierBillHover';
import './SupplierBillHover.css';

const fmt = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

const fmtDate = (d: Date) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

function PaymentDueFlagTipBlocks({
  flag,
  effectiveDate,
}: {
  flag: PaymentDueFlag;
  effectiveDate: Date;
}) {
  const anchorLine = describeReceivableDueAnchor(flag);

  return (
    <>
      <div className="bp-tip-payment">
        <div className="bp-tip-payment-head">
          <span className="bp-tip-amt">
            {flag.label}
            <span className="bp-tip-pct"> · {fmt(flag.amount)}</span>
          </span>
        </div>
        {flag.detail?.trim() && (
          <span className="bp-tip-payment-sub">{flag.detail.trim()}</span>
        )}
      </div>
      <div className="bp-tip-divider" />
      <div className="bp-tip-payment">
        <span className="bp-tip-payment-sub">
          <strong>Forecast due</strong>
          {' — '}
          {fmtDate(effectiveDate)}
          {flag.estimated ? ' (estimated)' : ''}
        </span>
      </div>
      <div className="bp-tip-payment">
        <span className="bp-tip-payment-sub">
          <strong>Tied to</strong>
          {' — '}
          {anchorLine}
        </span>
      </div>
      {flag.estimated && flag.kind === 'installment' && flag.installmentId !== 'deposit' && (
        <span className="bp-tip-note">
          {flag.installmentId === 'pre_shipment'
            ? 'Estimate — no Production task group or Shipment/Production Complete milestone found; using the schedule end.'
            : 'Estimate — no Installation/Delivery Complete milestone found; using the schedule end.'}
        </span>
      )}
      {typeof flag.lagBusinessDays === 'number' && flag.lagBusinessDays > 0 && (
        <span className="bp-tip-note">
          Includes +{flag.lagBusinessDays} business day{flag.lagBusinessDays === 1 ? '' : 's'} payment lag
          (Settings → Project Defaults).
        </span>
      )}
    </>
  );
}

export function PaymentDueFlagTipContent({
  flag,
  effectiveDate,
}: {
  flag: PaymentDueFlag;
  effectiveDate: Date;
}) {
  return (
    <div className="bp-tip-schedule">
      <PaymentDueFlagTipBlocks flag={flag} effectiveDate={effectiveDate} />
    </div>
  );
}

export function PaymentDueFlagsDayTipContent({
  items,
}: {
  items: Array<{ flag: PaymentDueFlag; effectiveDate: Date }>;
}) {
  if (items.length === 1) {
    return <PaymentDueFlagTipContent flag={items[0]!.flag} effectiveDate={items[0]!.effectiveDate} />;
  }
  return (
    <div className="bp-tip-schedule">
      {items.map((item, i) => (
        <React.Fragment key={item.flag.installmentId}>
          {i > 0 && <div className="bp-tip-divider" />}
          <PaymentDueFlagTipBlocks flag={item.flag} effectiveDate={item.effectiveDate} />
        </React.Fragment>
      ))}
    </div>
  );
}

/** Payment flag shown inside a day header cell (replaces the date number). */
export function GanttPaymentDayCell({
  items,
  compact,
  badge,
  extraTip,
}: {
  items: Array<{ flag: PaymentDueFlag; effectiveDate: Date }>;
  /** Hide percentage label when the day column is narrow. */
  compact?: boolean;
  /** Extra marker on the flag, such as a meeting +1. */
  badge?: React.ReactNode;
  /** Appended under the payment tip (meeting details for that day). */
  extraTip?: React.ReactNode;
}) {
  const estimated = items.some((item) => item.flag.estimated);
  const singleLabel = items.length === 1 ? items[0]!.flag.label : null;
  const paymentLabel =
    items.length === 1 ? `Payment due ${singleLabel}` : `${items.length} payments due`;

  return (
    <HoverTip
      tip={
        <>
          <PaymentDueFlagsDayTipContent items={items} />
          {extraTip ? (
            <>
              <div className="bp-tip-divider" />
              {extraTip}
            </>
          ) : null}
        </>
      }
      wide
    >
      <span className="pm-gantt-day-payment-wrap">
        <span
          className={`pm-gantt-day-payment ${estimated ? 'pm-gantt-day-payment--estimated' : ''}`}
          aria-label={badge ? `${paymentLabel}, plus other items this day` : paymentLabel}
        >
          <Flag size={11} strokeWidth={2.25} className="pm-gantt-day-payment-icon" aria-hidden />
          {!compact && singleLabel && (
            <span className="pm-gantt-day-payment-label">{singleLabel}</span>
          )}
          {!compact && items.length > 1 && (
            <span className="pm-gantt-day-payment-label">{items.length}</span>
          )}
        </span>
        {badge}
      </span>
    </HoverTip>
  );
}
